import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import type { AssignedAccount } from '@/types/strategy';

interface AvailableAccount {
  id: string;
  platform: 'mt4' | 'ctrader';
  account_id: string;
  account_name: string;
  balance: number;
  currency: string;
  is_live: boolean;
}

interface AccountSelectorProps {
  selected: AssignedAccount[];
  onChange: (accounts: AssignedAccount[]) => void;
}



export default function AccountSelector({ selected, onChange }: AccountSelectorProps) {
  const [availableAccounts, setAvailableAccounts] = useState<AvailableAccount[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchAccounts() {
      const all: AvailableAccount[] = [];

      // Fetch MT4 accounts from user_connections
      try {
        const { data: mt4Data } = await supabase
          .from('user_connections')
          .select('id, data')
          .eq('type', 'mt4');

        if (mt4Data && mt4Data.length > 0) {
          mt4Data.forEach((conn) => {
            const d = conn.data as Record<string, unknown> || {};
            all.push({
              id: `mt4-${conn.id}`,
              platform: 'mt4',
              account_id: String(d.login || d.account_id || ''),
              account_name: String(d.account_name || d.name || `MT4 ${d.login || ''}`),
              balance: Number(d.balance || 0),
              currency: String(d.currency || 'USD'),
              is_live: Boolean(d.is_live ?? false),
            });
          });
        }
      } catch {
        // fallback to mock
      }



      // Fetch cTrader accounts from ctrader_gateway_accounts
      try {
        const { data: ctData } = await supabase
          .from('ctrader_gateway_accounts')
          .select('*');

        if (ctData && ctData.length > 0) {
          ctData.forEach((acc) => {
            all.push({
              id: `ct-${acc.id}`,
              platform: 'ctrader',
              account_id: String(acc.account_id || ''),
              account_name: String(acc.account_name || ''),
              balance: Number(acc.balance || 0),
              currency: String(acc.currency || 'USD'),
              is_live: Boolean(acc.is_live),
            });
          });
        }
      } catch {
        // fallback to mock
      }



      setAvailableAccounts(all);
      setLoading(false);
    }

    fetchAccounts();
  }, []);

  function isSelected(acc: AvailableAccount): boolean {
    return selected.some(
      (s) => s.platform === acc.platform && s.account_id === acc.account_id,
    );
  }

  function handleToggle(acc: AvailableAccount) {
    if (isSelected(acc)) {
      onChange(
        selected.filter(
          (s) => !(s.platform === acc.platform && s.account_id === acc.account_id),
        ),
      );
    } else {
      const newAccount: AssignedAccount = {
        id: `asgn-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
        platform: acc.platform,
        account_id: acc.account_id,
        account_name: acc.account_name,
        balance: acc.balance,
        currency: acc.currency,
        is_live: acc.is_live,
      };
      onChange([...selected, newAccount]);
    }
  }

  const mt4Accounts = availableAccounts.filter((a) => a.platform === 'mt4');
  const ctraderAccounts = availableAccounts.filter((a) => a.platform === 'ctrader');
  const hasAnyEnabled = selected.length > 0;

  return (
    <div className="space-y-5">
      {/* Copy Trade Toggles */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-foreground-800 font-medium">MT4 Copy Trade</p>
            <p className="text-foreground-500 text-[10px] mt-0.5">Forward signals to MT4 accounts</p>
          </div>
          <button
            type="button"
            onClick={() => {
              if (hasAnyEnabled && selected.some((a) => a.platform === 'mt4')) {
                onChange(selected.filter((a) => a.platform !== 'mt4'));
              }
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 cursor-pointer ${
              selected.some((a) => a.platform === 'mt4') ? 'bg-accent-500' : 'bg-background-300'
            }`}
          >
            <span
              className={`inline-block rounded-full bg-background-50 transition-transform duration-200 ${
                selected.some((a) => a.platform === 'mt4') ? 'translate-x-[22px]' : 'translate-x-[3px]'
              }`}
              style={{ width: '18px', height: '18px' }}
            ></span>
          </button>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-foreground-800 font-medium">cTrader Copy Trade</p>
            <p className="text-foreground-500 text-[10px] mt-0.5">Forward signals to cTrader accounts</p>
          </div>
          <button
            type="button"
            onClick={() => {
              if (hasAnyEnabled && selected.some((a) => a.platform === 'ctrader')) {
                onChange(selected.filter((a) => a.platform !== 'ctrader'));
              }
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 cursor-pointer ${
              selected.some((a) => a.platform === 'ctrader') ? 'bg-emerald-500' : 'bg-background-300'
            }`}
          >
            <span
              className={`inline-block rounded-full bg-background-50 transition-transform duration-200 ${
                selected.some((a) => a.platform === 'ctrader') ? 'translate-x-[22px]' : 'translate-x-[3px]'
              }`}
              style={{ width: '18px', height: '18px' }}
            ></span>
          </button>
        </div>
      </div>

      {/* Available Accounts */}
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* MT4 Section */}
          {mt4Accounts.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <div className="w-4 h-4 rounded bg-accent-500/10 flex items-center justify-center">
                  <i className="ri-terminal-box-line text-accent-400 text-[10px]"></i>
                </div>
                <span className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider">MT4 Accounts</span>
              </div>
              <div className="space-y-1.5">
                {mt4Accounts.map((acc) => {
                  const checked = isSelected(acc);
                  return (
                    <label
                      key={acc.id}
                      className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
                        checked
                          ? 'bg-accent-500/5 border-accent-500/20'
                          : 'bg-background-50 border-background-200/70 hover:border-background-300/60'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleToggle(acc)}
                        className="w-4 h-4 rounded border-background-300 text-accent-500 focus:ring-accent-500/30 cursor-pointer accent-accent-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-semibold text-foreground-800 truncate">{acc.account_name}</span>
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${acc.is_live ? 'bg-emerald-400' : 'bg-foreground-300'}`}></span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] font-mono text-foreground-500">{acc.account_id}</span>
                          <span className="text-[10px] text-foreground-400">
                            {Number(acc.balance).toLocaleString('en-US', { minimumFractionDigits: 2 })} {acc.currency}
                          </span>
                        </div>
                      </div>
                      <span className={`shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                        acc.is_live
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : 'bg-background-200/70 text-foreground-500'
                      }`}>
                        {acc.is_live ? 'Live' : 'Demo'}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* cTrader Section */}
          {ctraderAccounts.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <div className="w-4 h-4 rounded bg-emerald-500/10 flex items-center justify-center">
                  <i className="ri-exchange-funds-line text-emerald-400 text-[10px]"></i>
                </div>
                <span className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider">cTrader Accounts</span>
              </div>
              <div className="space-y-1.5">
                {ctraderAccounts.map((acc) => {
                  const checked = isSelected(acc);
                  return (
                    <label
                      key={acc.id}
                      className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
                        checked
                          ? 'bg-emerald-500/5 border-emerald-500/20'
                          : 'bg-background-50 border-background-200/70 hover:border-background-300/60'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleToggle(acc)}
                        className="w-4 h-4 rounded border-background-300 text-emerald-500 focus:ring-emerald-500/30 cursor-pointer accent-emerald-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-semibold text-foreground-800 truncate">{acc.account_name}</span>
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${acc.is_live ? 'bg-emerald-400' : 'bg-foreground-300'}`}></span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] font-mono text-foreground-500">{acc.account_id}</span>
                          <span className="text-[10px] text-foreground-400">
                            {Number(acc.balance).toLocaleString('en-US', { minimumFractionDigits: 2 })} {acc.currency}
                          </span>
                        </div>
                      </div>
                      <span className={`shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                        acc.is_live
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : 'bg-background-200/70 text-foreground-500'
                      }`}>
                        {acc.is_live ? 'Live' : 'Demo'}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {availableAccounts.length === 0 && (
            <div className="bg-background-50 rounded-lg border border-dashed border-background-300/60 p-4 text-center">
              <div className="w-8 h-8 rounded-lg bg-background-200/70 flex items-center justify-center mx-auto mb-2">
                <i className="ri-link-unlink text-foreground-400 text-sm"></i>
              </div>
              <p className="text-foreground-500 text-xs">No trading accounts available</p>
              <p className="text-foreground-400 text-[10px] mt-0.5">
                Connect MT4 or cTrader accounts in Account Management first.
              </p>
            </div>
          )}

          {hasAnyEnabled && (
            <div className="bg-accent-500/5 border border-accent-500/15 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <i className="ri-information-line text-accent-400 text-sm mt-0.5"></i>
                <p className="text-xs text-foreground-600">
                  <span className="font-semibold text-foreground-700">{selected.length}</span> account{selected.length !== 1 ? 's' : ''} selected.
                  Trade signals will be forwarded to all checked accounts for copy trading.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}